/**
 * WorkspaceHeader Component
 * Notion-style top bar with search, nav, peek toggles, and user menu.
 */
import React, { useState } from 'react';
import { Box, Group, Text, TextInput, ActionIcon, Avatar, Menu, Divider } from '@mantine/core';
import { IconSearch, IconQuestionMark, IconBell, IconMenu2, IconChevronDown, IconSun, IconMoon, IconSparkles, IconPlus, IconUser, IconSettings, IconLogout } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import { usePeek } from '../common/PeekManager';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';

export const WorkspaceHeader: React.FC = () => {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { isPeekOpen, togglePeek } = usePeek();
  const { toggleSidebar, goToHome, pages, selectPage } = useWorkspace();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  const bg = colorScheme === 'dark' ? '#202124' : '#ffffff';
  const border = colorScheme === 'dark' ? '#3c4043' : 'var(--mantine-color-gray-3)';
  const text = colorScheme === 'dark' ? '#e8eaed' : '#202124';
  const inputBg = colorScheme === 'dark' ? '#303134' : '#f1f3f4';

  return (
    <Box style={{ height: 50, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', padding: '0 16px', backgroundColor: bg, color: text, gap: 12 }}>
      {/* Left — toggle + brand */}
      <Group gap="xs" style={{ flex: '0 0 auto' }}>
        <ActionIcon variant="subtle" size="md" title="Menu" onClick={toggleSidebar}><IconMenu2 size={18} /></ActionIcon>
        <Text fw={600} size="sm" style={{ letterSpacing: '-0.01em', cursor: 'pointer' }} onClick={goToHome}>FreshCards</Text>
        <ActionIcon variant="subtle" size="sm" title="Workspace options" onClick={() => {}}><IconChevronDown size={14} /></ActionIcon>
      </Group>

      {/* Center — search */}
      <Box style={{ flex: 1, maxWidth: 600, margin: '0 auto', position: 'relative' }}>
        <TextInput
          placeholder="Search pages..."
          size="sm"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setShowResults(e.target.value.length > 0); }}
          styles={{ root: { width: '100%' }, input: { borderRadius: 4, backgroundColor: inputBg, border: searchFocused ? '1px solid #1a73e8' : '1px solid transparent' } }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
        />
        {showResults && searchQuery && (
          <Box style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: bg, borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 300, overflow: 'auto', zIndex: 1000, marginTop: 4 }}>
            {pages.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 5).map(page => (
              <Box key={page.id} p="sm" style={{ cursor: 'pointer', borderBottom: '1px solid #e9e9e7' }}
                onClick={() => { setSearchQuery(''); setShowResults(false); selectPage(page.id); }}>
                <Text size="sm">{page.title}</Text>
              </Box>
            ))}
            {pages.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
              <Box p="sm"><Text size="sm" c="dimmed">No pages found</Text></Box>
            )}
          </Box>
        )}
      </Box>

      {/* Right — actions */}
      <Group gap="xs" style={{ flex: '0 0 auto' }}>
        <ActionIcon variant="subtle" size="md" title="Help" onClick={() => alert('FreshCards v1.0\nKeyboard: Ctrl+N, Ctrl+S, Ctrl+/')}><IconQuestionMark size={18} /></ActionIcon>
        <ActionIcon variant="subtle" size="md" title="Notifications" onClick={() => alert('No notifications')}><IconBell size={18} /></ActionIcon>
        <Divider orientation="vertical" style={{ height: 24 }} />
        <ActionIcon variant={isPeekOpen('quick-note') ? 'filled' : 'subtle'} color="blue" onClick={() => togglePeek('quick-note')} size="md" title="Quick Note"><IconPlus size={18} /></ActionIcon>
        <ActionIcon variant={isPeekOpen('ai-chat') ? 'filled' : 'subtle'} color="grape" onClick={() => togglePeek('ai-chat')} size="md" title="AI Chat"><IconSparkles size={18} /></ActionIcon>
        <ActionIcon variant="subtle" onClick={() => toggleColorScheme()} size="md" title={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}>
          {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
        <Menu position="bottom-end" shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant="subtle" size="md" radius="xl"><Avatar size={28} color="blue" radius="xl">U</Avatar></ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Signed in as User</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={() => alert('Profile page coming soon!')}>Profile</Menu.Item>
            <Menu.Item leftSection={<IconSettings size={14} />} onClick={() => alert('Settings page coming soon!')}>Settings</Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={() => alert('Signed out!')}>Sign out</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
};